/**
 * @file       Cypher Share API service
 * @version    1.0.0
 * @author     Boris Veits
 * @copyright  Copyright (c) 2018-2026, MindShare HDV LLC. All rights
 *             reserved.
 */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '@environments/environment';
import { SHARE } from '@app/constants/Api.constant';
import { CyResponse } from '@app/secure/interface/CyResponse.interface';
import { CyShareCreateRequest }
    from '@app/secure/interface/CyShareCreateRequest.interface';
import { CyShareRedeemRequest }
    from '@app/secure/interface/CyShareRedeemRequest.interface';
import { CyShareRedeemData }
    from '@app/secure/interface/CyShareRedeemResponse.interface';
import
{
    CyShareCreateCyData,
    CyShareRedeemSessionCyData
} from '@app/secure/interface/CyShareServiceResponses.interface';

@Injectable(
{
    providedIn: 'root'
})
class CyShareService
{
    /**
     * @desc   - construct the share API service
     * @access - public
     * @param  - http : HttpClient
     * @return -
     */
    constructor
    (
        private http: HttpClient
    )
    {}

    /**
     * @desc   - create a Cypher Share and send link plus code
     * @access - public
     * @param  - body : create payload
     * @return - Observable CyResponse
     */
    public createShare
    (
        body: CyShareCreateRequest
    ): Observable<CyResponse<CyShareCreateCyData>>
    {
        let request$;

        request$ = this.http.post<CyResponse<CyShareCreateCyData>>(
            `${environment.apiUrl}/${SHARE.CREATE_SHARE}`,
            body,
            { withCredentials: true }
        );

        return request$;
    }

    /**
     * @desc   - redeem a share with contact and access code
     * @access - public
     * @param  - body : redeem payload
     * @return - Observable CyResponse
     */
    public redeemShare
    (
        body: CyShareRedeemRequest
    ): Observable<CyResponse<CyShareRedeemSessionCyData>>
    {
        let request$;

        request$ = this.http.post<CyResponse<CyShareRedeemSessionCyData>>(
            `${environment.apiUrl}/${SHARE.REDEEM_SHARE}`,
            body,
            { withCredentials: true }
        );

        return request$;
    }

    /**
     * @desc   - list entities for the redeemed share session
     * @access - public
     * @param  -
     * @return - Observable CyResponse
     */
    public getSharedEntities():
        Observable<CyResponse<CyShareRedeemData>>
    {
        let request$: Observable<CyResponse<CyShareRedeemData>>;

        request$ = this.http.post<CyResponse<CyShareRedeemData>>(
            `${environment.apiUrl}/${SHARE.GET_SHARED_ENTITIES}`,
            {},
            { withCredentials: true }
        );

        return request$;
    }
}

export { CyShareService };
